import React from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { ReactComponent as ChevronRight } from "../../assets/icons/chevron-right.svg";
import "./Breadcrumb.scss";

interface BreadcrumbProps {
  showTitle?: boolean;
  showBreadcrumbs?: boolean;
}

interface BreadcrumbItem {
  name: string;
  path: string;
  isCurrentPage?: boolean;
  isJobCode: boolean;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({
  showTitle = true,
  showBreadcrumbs = true,
}) => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const pathnames = location.pathname
    .split("/")
    .filter((x) => x)
    .map((path) => decodeURIComponent(path));

  const getBreadcrumbName = (path: string, index: number): string => {
    // Handle dynamic parameters
    if (path === params.jobCode) {
      return path;
    }
    if (path === params.tripId) {
      return "";
    }

    // Skip showing "containers" in all cases
    if (path === "containers") {
      return "";
    }

    switch (path) {
      case "dashboard":
        return "Dashboard";
      case "jobs":
        return "Jobs";
      case "all":
        return "All";
      case "initiated":
        return "Initiated";
      case "assigned":
        return "Assigned";
      case "in-progress":
        return "In Progress";
      case "completed":
        return "Completed";
      case "cancelled":
        return "Cancelled";
      case "deleted":
        return "Deleted";
      case "create-new":
        return "Create New Job";
      case "edit":
        return "Edit Job";
      case "view":
        return "View Job";
      case "containers":
        return "";
      case "assign-containers":
        return "Assign Containers";
      case "trips":
        return "Trips";
      case "active":
        return "Active";
      case "history":
        return "History";
      case "trip-history":
        return "Trip History";
      case "profile":
        return "Profile";
      case "settings":
        return "Settings";
      case "reports":
        return "Reports";
      case "live-tracking":
        return "Live Tracking";
      case "upload":
        return "Upload";
      case "transporters":
        return "Transporters";
      case "drivers":
        return "Drivers";
      case "trucks":
        return "Trucks";
      case "federations":
        return "Federations";
      case "fleet":
        return "Fleet";
      case "locations":
        return "Locations";
      case "create":
        return "Create";
      case "instructions":
        return "Instructions";
      default:
        // Skip jobId in breadcrumb display
        if (path === params.jobId) {
          return "";
        }
        // Handle generic edit, view, and details cases
        if (path === "edit") {
          if (pathnames.includes("transporters")) {
            return "Edit Transporter";
          } else if (pathnames.includes("drivers")) {
            return "Edit Driver";
          } else if (pathnames.includes("trucks")) {
            return "Edit Truck";
          } else if (pathnames.includes("jobs")) {
            return "Edit Job";
          }
          return "Edit";
        }
        if (path === "view") {
          if (pathnames.includes("jobs")) {
            return "View Job";
          }
          return "View";
        }
        if (path === "details") {
          if (pathnames.includes("transporters")) {
            return "Transporter Details";
          } else if (pathnames.includes("drivers")) {
            return "Driver Details";
          } else if (pathnames.includes("trucks")) {
            return "Truck Details";
          }
          return "Details";
        }
        return path;
    }
  };

  const getRouteTo = (index: number): string => {
    const route = `/${pathnames.slice(0, index + 1).join("/")}`;

    // Special handling for job code to navigate to the job containers page
    if (pathnames[index] === params.jobCode) {
      return `/jobs/all/${params.jobId}/${params.jobCode}/containers`;
    }

    return route;
  };

  // Special handling for job routes to ensure consistent breadcrumbs
  const shouldShowBreadcrumb = () => {
    const path = location.pathname;
    return (
      path.startsWith("/dashboard") ||
      path.startsWith("/jobs/") ||
      path.startsWith("/job/") ||
      path.startsWith("/trip-history/job/") ||
      path.startsWith("/trips/") ||
      path.startsWith("/profile") ||
      path.startsWith("/transporters") ||
      path.startsWith("/drivers") ||
      path.startsWith("/trucks") ||
      path.startsWith("/federations") ||
      path.startsWith("/fleet") ||
      path.startsWith("/locations") ||
      path.startsWith("/reports") ||
      path.startsWith("/settings") ||
      path.startsWith("/instructions")
    );
  };

  if (!shouldShowBreadcrumb()) {
    return null;
  }

  // Get the current page title (last breadcrumb)
  const currentPageTitle = getBreadcrumbName(
    pathnames[pathnames.length - 1],
    pathnames.length - 1
  );

  // Get the breadcrumb items without the last one (which is the current page)
  const breadcrumbItems: BreadcrumbItem[] = pathnames
    .slice(0, -1)
    .map((name, index) => {
      const routeTo = getRouteTo(index);
      const breadcrumbName = getBreadcrumbName(name, index);

      // Skip rendering if the breadcrumb name is empty (for jobId or containers)
      if (!breadcrumbName) {
        return null;
      }

      const isJobCode = name === params.jobCode;
      // If this is a job code and the next item is containers, make it the active item
      // But only if we're not on the live-tracking page
      const isActive =
        isJobCode &&
        pathnames[index + 1] === "containers" &&
        pathnames[pathnames.length - 1] !== "live-tracking";

      return {
        name: breadcrumbName,
        path: routeTo,
        isJobCode,
        isCurrentPage: isActive,
      } as BreadcrumbItem;
    })
    .filter((item): item is BreadcrumbItem => item !== null);

  // Add the current page as the last item only if it's not empty and not a job code
  let currentPage: BreadcrumbItem | null =
    currentPageTitle && !breadcrumbItems.some((item) => item.isCurrentPage)
      ? {
          name: currentPageTitle,
          path: location.pathname,
          isCurrentPage: true,
          isJobCode: false,
        }
      : null;

  // Force include "Live Tracking" if it's the last pathname
  if (pathnames[pathnames.length - 1] === "live-tracking" && !currentPage) {
    currentPage = {
      name: "Live Tracking",
      path: location.pathname,
      isCurrentPage: true,
      isJobCode: false,
    };
  }

  // Force include "Dashboard" if it's the last pathname
  if (pathnames[pathnames.length - 1] === "dashboard" && !currentPage) {
    currentPage = {
      name: "Dashboard",
      path: location.pathname,
      isCurrentPage: true,
      isJobCode: false,
    };
  }

  return (
    <div className="breadcrumb-container">
      {/* {showTitle && <h1 className="page-title">{currentPageTitle}</h1>} */}

      {showBreadcrumbs && (
        <div className="breadcrumb">
          {/* {!location.pathname.startsWith("/jobs/") && (
            <Link to="/jobs/all" className="breadcrumb-item">
              Jobs
            </Link>
          )} */}

          {breadcrumbItems.map((item, index) => (
            <React.Fragment key={item.path}>
              {index > 0 && <ChevronRight className="breadcrumb-separator" />}
              <Link
                to={item.path}
                className={`breadcrumb-item ${
                  item.isCurrentPage ? "active" : ""
                }`}
              >
                {item.name}
              </Link>
            </React.Fragment>
          ))}

          {currentPage && breadcrumbItems.length > 0 && (
            <ChevronRight className="breadcrumb-separator" />
          )}
          {currentPage && (
            <span className="breadcrumb-item active">{currentPage.name}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default Breadcrumb;
